import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hold an object URL and revoke it when it's replaced or the component unmounts.
 *
 * MDRS-503 moved PHI documents from signed Azure URLs to `blob:` object URLs
 * fetched through the authenticated proxy. An object URL keeps its blob alive
 * for the life of the *document*, not the component — so without an explicit
 * revoke, a Document Sorter session over a case bundle leaks one full PDF per
 * file the user opens.
 *
 * The revoke is **deferred a tick**: an `<iframe>`/viewer that is still tearing
 * down will fault if the bytes are pulled out from under it, and React has
 * already re-rendered by the time the cleanup runs. This mirrors the pattern in
 * `pages/ReportStudio/ReportBuilder/index.tsx`, which hit exactly that.
 *
 * Only `blob:` URLs are revoked — passing a plain `https:` URL is safe, so
 * callers that sometimes have one and sometimes the other don't need to branch.
 */
export function useObjectUrl(): [string | null, (url: string | null) => void] {
  const [url, setUrlState] = useState<string | null>(null);
  // Tracked in a ref as well as state: the revoke has to see the outgoing URL
  // from inside a timeout that outlives the render which replaced it.
  const currentRef = useRef<string | null>(null);

  const revokeLater = useCallback((stale: string | null) => {
    if (!stale || !stale.startsWith("blob:")) return;
    setTimeout(() => URL.revokeObjectURL(stale), 500);
  }, []);

  const setUrl = useCallback(
    (next: string | null) => {
      const stale = currentRef.current;
      if (stale === next) return;
      currentRef.current = next;
      setUrlState(next);
      revokeLater(stale);
    },
    [revokeLater]
  );

  useEffect(
    () => () => {
      revokeLater(currentRef.current);
      currentRef.current = null;
    },
    [revokeLater]
  );

  return [url, setUrl];
}
